import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Paper, Stack, TextField, Typography } from '@mui/material';
import { ContentCopy, Refresh } from '@mui/icons-material';
import axios from 'axios';

const InvitationManager: React.FC<{ slug: string }> = ({ slug }) => {
  const location = useLocation();
  const [code, setCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const request = useRef(0);
  const rotating = useRef(false);
  const path = `/api/events/${encodeURIComponent(slug)}/invitation-code`;
  const rsvpLink = `${window.location.origin}/rsvp/events/${encodeURIComponent(slug)}`;
  const invitationLink = code ? `${rsvpLink}?code=${encodeURIComponent(code)}` : '';

  const readCode = useCallback(async () => {
    const { data } = await axios.get(path);
    if (typeof data?.invitation_code !== 'string' || !data.invitation_code) throw new Error('Missing invitation code');
    return data.invitation_code as string;
  }, [path]);

  const load = useCallback(async () => {
    const current = ++request.current;
    setLoading(true);
    setError('');
    try {
      const invitation = await readCode();
      if (current === request.current) setCode(invitation);
    } catch {
      if (current === request.current) setError('Unable to load the invitation code. Please try again.');
    } finally {
      if (current === request.current) setLoading(false);
    }
  }, [readCode]);

  useEffect(() => { load(); return () => { request.current += 1; }; }, [load]);

  const copy = async (text: string, label: string) => {
    setMessage(''); setError('');
    try {
      await navigator.clipboard.writeText(text);
      setMessage(`${label} copied.`);
    } catch { setError('Copy failed. Select and copy the text above.'); }
  };

  const regenerate = async () => {
    if (rotating.current) return;
    rotating.current = true;
    const current = ++request.current;
    setBusy(true); setError(''); setMessage('');
    let rotated = false;
    try {
      await axios.post(`${path}/regenerate`, {});
      rotated = true;
      if (current !== request.current) return;
      // Never keep showing the obsolete code after a successful rotation.
      setCode(null);
      const invitation = await readCode();
      if (current === request.current) {
        setCode(invitation);
        setMessage('Invitation code regenerated. Share the new code with your guests.');
      }
    } catch {
      if (current === request.current) setError(rotated
        ? 'The code was regenerated, but could not be loaded. Please reload the code before sharing it.'
        : 'Unable to regenerate the invitation code. Please try again.');
    } finally {
      rotating.current = false;
      if (current === request.current) { setBusy(false); setConfirm(false); }
    }
  };

  return <Paper sx={{ p: 3, mb: 3 }}>
    <Typography variant="h6" component="h2" sx={{ mb: 1 }}>Event invitation</Typography>
    {(location.state as { eventCreated?: boolean } | null)?.eventCreated && <Alert severity="success" sx={{ mb: 2 }}>
      Event created. Share the invitation code and RSVP link below with your guests.
    </Alert>}
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
      Share the invitation link to let guests open this event automatically, or share the RSVP link and code separately. Anyone with the invitation link can access this event.
    </Typography>
    <Stack spacing={2}>
      <Box data-testid="invitation-copy-grid" sx={{
        display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr) 48px', sm: 'minmax(0, 1fr) max-content' },
        alignItems: 'center', columnGap: 1, rowGap: 2,
        '& .MuiTextField-root': { minWidth: 0 },
        '& > .MuiButton-root': {
          justifyContent: { xs: 'center', sm: 'flex-start' }, whiteSpace: 'nowrap', minWidth: 0,
          '& .MuiButton-startIcon': { margin: { xs: 0, sm: '0 8px 0 -4px' } },
        },
      }}>
        <TextField fullWidth label="Invitation code" value={code || ''} placeholder={loading ? 'Loading…' : busy ? 'Regenerating…' : 'Code unavailable'}
          InputLabelProps={{ shrink: true }} InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.875rem' } }} inputProps={{ 'aria-label': 'Invitation code' }} />
        <Button aria-label="Copy code" title="Copy code" startIcon={<ContentCopy />} onClick={() => code && copy(code, 'Invitation code')} disabled={!code || busy || loading}><Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Copy code</Box></Button>
        <TextField fullWidth label="RSVP link" value={rsvpLink} InputProps={{ readOnly: true }} inputProps={{ 'aria-label': 'RSVP link' }} />
        <Button aria-label="Copy link" title="Copy link" startIcon={<ContentCopy />} onClick={() => copy(rsvpLink, 'RSVP link')}><Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Copy link</Box></Button>
        <TextField fullWidth label="Invitation link" value={invitationLink} InputProps={{ readOnly: true }} inputProps={{ 'aria-label': 'Invitation link' }} />
        <Button aria-label="Copy invitation link" title="Copy invitation link" startIcon={<ContentCopy />} onClick={() => invitationLink && copy(invitationLink, 'Invitation link')} disabled={!code || busy || loading}><Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Copy invitation link</Box></Button>
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      {message && <Alert severity="success" role="status">{message}</Alert>}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Button variant="outlined" startIcon={<Refresh />} onClick={() => setConfirm(true)} disabled={loading || busy || !code}>Regenerate code</Button>
        {!code && !loading && !busy && <Button onClick={load}>Reload code</Button>}
      </Box>
    </Stack>
    <Dialog open={confirm} onClose={() => { if (!busy) setConfirm(false); }} aria-labelledby="regenerate-title" aria-describedby="regenerate-description">
      <DialogTitle id="regenerate-title">Regenerate invitation code?</DialogTitle>
      <DialogContent><DialogContentText id="regenerate-description">
        The previous invitation code will stop working. Guests who already opened the event will need the new code too. Existing RSVPs will not be changed.
      </DialogContentText></DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirm(false)} disabled={busy}>Cancel</Button>
        <Button onClick={regenerate} variant="contained" disabled={busy}>{busy ? 'Regenerating…' : 'Regenerate'}</Button>
      </DialogActions>
    </Dialog>
  </Paper>;
};

export default InvitationManager;
